import { animate, state, style, transition, trigger } from "@angular/animations";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { FetchMessagesUnreadCount } from "@app/store/actions/messages-unread-count.actions";
import { selectMessagesUnreadCount } from "@app/store/selectors/app/app.selectors";
import { MainState } from "@app/store/state";
import { UserProfileInterface } from "@core/interfaces/user-profile.interface";
import { UserInterface } from "@core/interfaces/user.interface";
import { AuthService } from "@core/services/auth.service";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { DeviceService } from "@core/services/device.service";
import { LoadingService } from "@core/services/loading.service";
import { LocalStorageService } from "@core/services/localstorage.service";
import { MobilePageMenuConfig, MobilePageMenuService } from "@core/services/mobile-page-menu.service";
import { ScrollHideService } from "@core/services/scroll-hide.service";
import { SwipeToCloseService } from "@core/services/swipe-to-close.service";
import { UserService } from "@core/services/user.service";
import { VersionCheckService } from "@core/services/version-check.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { environment } from "@env/environment";
import { selectCurrentUser } from "@features/account/store/auth.selectors";
import { selectUnreadNotificationsCount } from "@features/notifications/store/notifications.selectors";
import { NgbModal, NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { Observable } from "rxjs";
import { delay, filter, map, take, takeUntil } from "rxjs/operators";

interface LanguageInterface {
  code: string;
  label: string;
}

@Component({
  selector: "astrobin-mobile-header",
  templateUrl: "./mobile-header.component.html",
  styleUrls: ["./mobile-header.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("slideInOut", [
      state(
        "in",
        style({
          transform: "translateX(0)",
          opacity: 1
        })
      ),
      state(
        "out",
        style({
          transform: "translateX(100%)",
          opacity: 0
        })
      ),
      transition("out => in", [
        style({ transform: "translateX(100%)" }),
        animate(
          "200ms ease-in-out",
          style({
            transform: "translateX(0)",
            opacity: 1
          })
        )
      ]),
      transition("in => out", [
        animate(
          "200ms ease-in-out",
          style({
            transform: "translateX(100%)",
            opacity: 0
          })
        )
      ])
    ]),
    trigger("fadeInOut", [
      transition(":enter", [
        style({ opacity: 0 }),
        animate("300ms 100ms ease-in-out", style({ opacity: 1 })) // 100ms delay, 300ms duration for fade in
      ]),
      transition(":leave", [
        animate("150ms ease-in-out", style({ opacity: 0 })) // 150ms fade out
      ])
    ])
  ]
})
export class MobileHeaderComponent extends BaseComponentDirective implements OnInit, OnDestroy {
  user: UserInterface;
  userProfile: UserProfileInterface;
  currentUserWrapper: { user: UserInterface | null; userProfile: UserProfileInterface | null };
  @ViewChild("menuOffcanvas") menuOffcanvasTemplate: TemplateRef<any>;
  @ViewChild("loginRegisterMenu") loginRegisterMenuTemplate: TemplateRef<any>;
  @ViewChild("notificationsOffcanvas") notificationsOffcanvas: TemplateRef<any>;
  @ViewChild("versionUpdateModal") versionUpdateModal: TemplateRef<any>;
  @ViewChild("versionUpdateOffcanvas") versionUpdateOffcanvas: TemplateRef<any>;

  @HostBinding("class.header-hidden") isHeaderHidden = false;

  // Reference to the current offcanvas instance
  private activeOffcanvas: any = null;

  // Menu state
  currentMenuView: "main" | "language" | "help" | "moderate" = "main";

  // Page menu state
  pageMenuConfig$: Observable<MobilePageMenuConfig>;
  hasPageMenu$: Observable<boolean>;
  pageMenuOpenState$: Observable<boolean>;

  // PWA mode state
  isPwaMode$: Observable<boolean>;

  // Kebab menu tooltip state
  showKebabMenuTooltip = false;
  private readonly KEBAB_TOOLTIP_DISMISSED_KEY = "astrobin-kebab-menu-tooltip-dismissed";
  private readonly KEBAB_TOOLTIP_SHOWN_DATES_KEY = "astrobin-kebab-menu-tooltip-shown-dates";
  private readonly KEBAB_TOOLTIP_MAX_DAYS = 4;

  protected unreadNotificationsCount$: Observable<number> = this.store$
    .select(selectUnreadNotificationsCount)
    .pipe(takeUntil(this.destroyed$));

  protected unreadMessagesCount$: Observable<number> = this.store$
    .select(selectMessagesUnreadCount)
    .pipe(takeUntil(this.destroyed$));

  // Language selector properties
  languages: LanguageInterface[] = [];
  currentLanguageCodeDisplay: string;
  helpWithTranslationsUrl = "https://translate.astrobin.com/projects/astrobin/astrobin";

  // Footer information
  readonly currentYear: number = new Date().getFullYear();
  readonly buildVersion: string = environment.buildVersion;

  // Check if we're on current user's gallery page
  isOnCurrentUserGalleryPage = false;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly authService: AuthService,
    public readonly loadingService: LoadingService,
    public readonly windowRefService: WindowRefService,
    public readonly translateService: TranslateService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly offcanvasService: NgbOffcanvas,
    public readonly modalService: NgbModal,
    public readonly deviceService: DeviceService,
    public readonly userService: UserService,
    public readonly versionCheckService: VersionCheckService,
    private router: Router,
    private swipeToCloseService: SwipeToCloseService,
    private scrollHideService: ScrollHideService,
    private mobilePageMenuService: MobilePageMenuService,
    private localStorageService: LocalStorageService
  ) {
    super(store$);
    this.isPwaMode$ = this.deviceService.isPwaMode$;
  }

  ngOnInit() {
    super.ngOnInit();

    // Fetch initial messages unread count
    this.store$.dispatch(new FetchMessagesUnreadCount());

    this.currentUserWrapper$.pipe(takeUntil(this.destroyed$)).subscribe(wrapper => {
      this.user = wrapper.user;
      this.userProfile = wrapper.userProfile;
      this.currentUserWrapper = wrapper;
      this.changeDetectorRef.markForCheck();

      // Check if we should show the kebab tooltip when user state changes
      this._checkKebabTooltipDisplay();
    });

    // Check if we're on current user's gallery page
    this._checkIfOnCurrentUserGallery();

    // Subscribe to router changes to update the check
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this._checkIfOnCurrentUserGallery();
        // Reset tooltip state first, then check if it should be displayed on the new route
        this.showKebabMenuTooltip = false;
        this.changeDetectorRef.markForCheck();
        this._checkKebabTooltipDisplay();
      });

    // Subscribe to header visibility changes from the service
    this.scrollHideService
      .getHeaderVisibility()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isHidden => {
        this.isHeaderHidden = isHidden;
        this.changeDetectorRef.markForCheck();
      });

    // Initialize the language selector
    this.initializeLanguageSelector();

    // Initialize page menu observables
    this.pageMenuConfig$ = this.mobilePageMenuService.getMenuConfig();
    this.hasPageMenu$ = this.pageMenuConfig$.pipe(
      map(config => !!config && !!config.template),
      takeUntil(this.destroyed$)
    );
    this.pageMenuOpenState$ = this.mobilePageMenuService.getMenuOpenState();

    // React to page menu availability changes
    this.hasPageMenu$
      .pipe(
        filter(hasMenu => hasMenu), // Only when a menu becomes available
        delay(1000) // Delay showing the tooltip to avoid immediate display
      )
      .subscribe(() => {
        this._checkKebabTooltipDisplay();
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  initializeLanguageSelector(): void {
    const supportedLanguages = [
      { code: "en", label: "English" },
      { code: "de", label: "Deutsch" },
      { code: "es", label: "Español" },
      { code: "fr", label: "Français" },
      { code: "it", label: "Italiano" },
      { code: "ja", label: "日本語" },
      { code: "nl", label: "Nederlands" },
      { code: "pl", label: "Polski" },
      { code: "pt", label: "Português" },
      { code: "ru", label: "Русский" },
      { code: "sq", label: "Shqip" },
      { code: "tr", label: "Türkçe" },
      { code: "zh_Hans", label: "简体中文" }
    ];

    this.languages = supportedLanguages;

    // Set current language display code
    const currentLang = this.translateService.currentLang || "en";
    const foundLang = this.languages.find(lang => lang.code === currentLang);
    this.currentLanguageCodeDisplay = foundLang ? foundLang.code.toUpperCase() : "EN";
  }

  getSetLanguageUrl(languageCode: string): string {
    // Get the current URL and append the language param
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", languageCode);
      return url.toString();
    }

    return `?lang=${languageCode}`;
  }

  // Add a flag to track if this is a view transition (not initial load)
  private isViewTransition = false;

  // Show the language submenu
  showLanguageMenu(): void {
    this.isViewTransition = true;
    this.currentMenuView = "language";
    this.changeDetectorRef.markForCheck();
  }

  // Show the help submenu
  showHelpMenu(): void {
    this.isViewTransition = true;
    this.currentMenuView = "help";
    this.changeDetectorRef.markForCheck();
  }

  // Show the moderate submenu
  showModerateMenu(): void {
    this.isViewTransition = true;
    this.currentMenuView = "moderate";
    this.changeDetectorRef.markForCheck();
  }

  // Return to the main menu from a submenu
  showMainMenu(): void {
    this.isViewTransition = true;
    this.currentMenuView = "main";
    this.changeDetectorRef.markForCheck();
  }

  // Expose the transition flag to the template
  isInViewTransition(): boolean {
    return this.isViewTransition;
  }

  // Open the main menu offcanvas
  openMainMenu() {
    // Reset to the main view first and disable transition animation
    this.currentMenuView = "main";
    this.isViewTransition = false;

    // Add class to prevent body scrolling
    this.addScrollLock();

    // Open the offcanvas
    this.activeOffcanvas = this.offcanvasService.open(this.menuOffcanvasTemplate, {
      position: "start",
      panelClass: "mobile-main-menu",
      backdrop: true
    });

    // Remove the scroll lock when the offcanvas is dismissed
    this.activeOffcanvas.dismissed.subscribe(() => {
      this.removeScrollLock();
      this.isViewTransition = false; // Reset on close
    });
  }

  openUserMenu() {
    if (this.user) {
      // Navigate to user gallery
      this.userService.openGallery(this.user.username, this.userProfile?.enableNewGalleryExperience);
    } else {
      // Add class to prevent body scrolling
      this.addScrollLock();

      // Open login/register menu
      const ref = this.offcanvasService.open(this.loginRegisterMenuTemplate, {
        position: "end",
        panelClass: "mobile-login-menu",
        backdrop: true
      });

      // Remove the class when the offcanvas is dismissed
      ref.dismissed.subscribe(() => {
        this.removeScrollLock();
      });
    }
  }

  openNotificationsOffcanvas(): void {
    // Add class to prevent body scrolling
    this.addScrollLock();

    const ref = this.offcanvasService.open(this.notificationsOffcanvas, {
      panelClass: "notifications-offcanvas",
      position: this.deviceService.offcanvasPosition()
    });

    // Remove the class when the offcanvas is dismissed OR closed
    ref.dismissed.subscribe(() => {
      this.removeScrollLock();
    });

    ref.closed.subscribe(() => {
      this.removeScrollLock();
    });
  }

  /**
   * Open the page-specific kebab menu (triggered by icon or service)
   */
  openPageMenu(): void {
    this.mobilePageMenuService.openMenu();
  }

  /**
   * Helper method to safely add scroll lock on body
   * Checks for browser environment to avoid SSR issues
   */
  private addScrollLock(): void {
    if (typeof document !== "undefined") {
      document.body.classList.add("offcanvas-open");
    }
  }

  /**
   * Helper method to safely remove scroll lock from body
   * Checks for browser environment to avoid SSR issues
   */
  private removeScrollLock(): void {
    if (typeof document !== "undefined") {
      document.body.classList.remove("offcanvas-open");
    }
  }

  protected onMobileMenuOpen(): void {
    // Any additional logic to handle menu opening
  }

  protected onMobileMenuClose(): void {
    // Any additional logic to handle menu closing
  }

  /**
   * Dismisses the kebab menu tooltip and updates localStorage
   */
  dismissKebabMenuTooltip(): void {
    this.showKebabMenuTooltip = false;
    this.localStorageService.setItem(this.KEBAB_TOOLTIP_DISMISSED_KEY, "true");
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Get the current menu ID for debugging purposes
   */
  getCurrentMenuId(): string | null {
    return this.mobilePageMenuService.getCurrentMenuId();
  }

  /**
   * Check if we're on the current user's gallery page
   */
  private _checkIfOnCurrentUserGallery(): void {
    const url = this.router.url;

    // Check if we're on a user gallery page
    if (url.startsWith("/u/")) {
      // Extract the username from the URL
      const urlParts = url.split("/");
      const galleryUsername = urlParts[2]?.split("?")[0]?.split("#")[0]; // Remove query params and fragments

      // Compare with current user
      this.store$
        .select(selectCurrentUser)
        .pipe(take(1))
        .subscribe(currentUser => {
          const wasOnCurrentUserGallery = this.isOnCurrentUserGalleryPage;
          this.isOnCurrentUserGalleryPage = currentUser && currentUser.username === galleryUsername;
          this.changeDetectorRef.markForCheck();
        });
    } else {
      this.isOnCurrentUserGalleryPage = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Check if the kebab menu tooltip should be displayed
   */
  private _checkKebabTooltipDisplay(): void {
    // Don't show tooltip if user is not authenticated
    if (!this.user) {
      this.showKebabMenuTooltip = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Check if hasPageMenu$ has been initialized yet
    if (!this.hasPageMenu$) {
      this.showKebabMenuTooltip = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Check if there's actually a page menu before proceeding
    this.hasPageMenu$.pipe(take(1)).subscribe(hasMenu => {
      if (!hasMenu) {
        this.showKebabMenuTooltip = false;
        this.changeDetectorRef.markForCheck();
        return;
      }

      this._evaluateTooltipDisplay();
    });
  }

  /**
   * Evaluate whether to show the tooltip (called only when page menu exists)
   */
  private _evaluateTooltipDisplay(): void {
    const tooltipDismissed = this.localStorageService.getItem(this.KEBAB_TOOLTIP_DISMISSED_KEY);

    if (tooltipDismissed === "true") {
      // User has explicitly dismissed the tooltip for good
      this.showKebabMenuTooltip = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Get stored dates when tooltip was shown
    const shownDatesJson = this.localStorageService.getItem(this.KEBAB_TOOLTIP_SHOWN_DATES_KEY) || "[]";
    let shownDates: string[] = [];

    try {
      shownDates = JSON.parse(shownDatesJson);
    } catch (e) {
      console.error("Error parsing stored dates", e);
    }

    // Get today's date as YYYY-MM-DD
    const today = new Date().toISOString().split("T")[0];

    // Check if we've already shown the tooltip today
    if (shownDates.includes(today)) {
      // Don't change showKebabMenuTooltip - keep whatever state it's in
      return;
    }

    // If we've shown it less than MAX_DAYS times, show it again
    if (shownDates.length < this.KEBAB_TOOLTIP_MAX_DAYS) {
      // Show the tooltip and record as shown
      this.showKebabMenuTooltip = true;
      this.changeDetectorRef.markForCheck();

      // Record today as shown
      shownDates.push(today);
      this.localStorageService.setItem(this.KEBAB_TOOLTIP_SHOWN_DATES_KEY, JSON.stringify(shownDates));
    } else {
      this.showKebabMenuTooltip = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  onLogoClick(event: Event): void {
    event.preventDefault();

    if (this.versionCheckService.isUpdateAvailable()) {
      this.showVersionUpdateDialog();
    } else {
      void this.router.navigate(["/"]);
    }
  }

  showVersionUpdateDialog(): void {
    if (this.deviceService.smMax()) {
      this.offcanvasService.open(this.versionUpdateOffcanvas, {
        position: "bottom",
        panelClass: "version-update-offcanvas"
      });
    } else {
      this.modalService.open(this.versionUpdateModal, {
        centered: true,
        size: "sm"
      });
    }
  }

  onRefreshClick(): void {
    this.versionCheckService.activateAndReload();
  }

  onIgnoreUpdateClick(): void {
    this.versionCheckService.dismissUpdate();
    this.modalService.dismissAll();
    this.offcanvasService.dismiss();
  }
}
